import {
  motion,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
} from "framer-motion";
import { useCallback, useState } from "react";
import { Link } from "react-router-dom";

import granLuxeHero960 from "../../../assets/images/granluxe/granluxe-hero-960.avif";
import granLuxeHero1440 from "../../../assets/images/granluxe/granluxe-hero-1440.avif";
import granLuxeHero1920 from "../../../assets/images/granluxe/granluxe-hero-1920.avif";
import { EASE_OUT, transitions } from "../../../components/motion";
import { cn } from "../../../utils/classnames";

/**
 * Hero stage for the GranLuxe vehicle.
 *
 * The artwork is a cut-out image (vehicle only, transparent background), so
 * everything a studio photo would give us is rebuilt here in CSS:
 *
 *   1. a near-black studio backdrop with a soft key light (the stage),
 *   2. a grey halo behind the vehicle — the "silhouette" glow,
 *   3. light pooling on the floor, a soft body shadow and a dark contact patch
 *      under each tyre, so the vehicle sits ON the floor,
 *   4. a floor reflection that follows the vehicle's angle,
 *   5. a light streak / glare across the bodywork, like showroom lighting,
 *   6. a faint film grain, which also stops the dark gradients from banding.
 *
 * Realism rule: the vehicle itself never floats or lifts — only the light
 * moves. A car that bobs up and down reads as a sticker, not a photo.
 *
 * Only the vehicle is a link; the stage is not, so clicking the empty backdrop
 * beside the booking form never navigates away.
 */

/**
 * Cut-out artwork (transparent background), resized from the 13650px master
 * so phones never have to decode a ~390 MB bitmap. Every file keeps the
 * master's exact framing (1.896 : 1), so all the % positions below stay valid.
 */
export const GRANLUXE_CABIN_IMAGE = granLuxeHero1440;
export const GRANLUXE_CABIN_SRCSET = [
  `${granLuxeHero960} 960w`,
  `${granLuxeHero1440} 1440w`,
  `${granLuxeHero1920} 1920w`,
].join(", ");

/** Deep studio backdrop — matches the near-black of the previous banner. */
const STAGE_BASE =
  "linear-gradient(180deg, #0B0C0E 0%, #121417 52%, #07080A 100%)";

/** Corner falloff, so the dark frame reads as a lit room and not a flat fill. */
const STAGE_VIGNETTE =
  "radial-gradient(90% 85% at 50% 0%, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0) 62%)," +
  "radial-gradient(110% 95% at 50% 100%, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0) 58%)";

/** Soft overhead light on the backdrop; it drifts with the pointer on desktop. */
const KEY_LIGHT =
  "radial-gradient(closest-side, rgba(220,226,235,0.07) 0%, rgba(220,226,235,0) 100%)";

/** The grey halo that sits *behind* the vehicle. */
const VEHICLE_HALO =
  "radial-gradient(closest-side, rgba(193,199,207,0.34) 0%, rgba(112,118,127,0.18) 40%, rgba(12,13,15,0) 73%)";

/** Light from above landing on the floor around the vehicle. */
const FLOOR_LIGHT_POOL =
  "radial-gradient(closest-side, rgba(150,158,170,0.16) 0%, rgba(150,158,170,0) 100%)";

/** Wide, soft shadow cast by the whole body. */
const BODY_SHADOW =
  "radial-gradient(closest-side, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 55%, rgba(0,0,0,0) 100%)";

/** Small, very dark patch where a tyre actually touches the floor. */
const TYRE_CONTACT_SHADOW =
  "radial-gradient(closest-side, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.6) 45%, rgba(0,0,0,0) 100%)";

/** Thin diagonal highlight that sweeps across the paint. */
const LIGHT_STREAK =
  "linear-gradient(105deg, rgba(255,255,255,0) 30%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0) 70%)";

/** Broad soft glare on the paint that follows the pointer while hovering. */
const PAINT_GLARE =
  "radial-gradient(closest-side, rgba(255,255,255,0.13) 0%, rgba(255,255,255,0) 100%)";

/** Static noise tile (SVG turbulence) — painted once, never animated. */
const FILM_GRAIN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

/**
 * Subtle grade so the photo matches the stage lighting, plus a 1px cool rim
 * light along the roofline that separates the black body from the black stage.
 */
const VEHICLE_GRADE =
  "contrast(1.04) saturate(1.05) drop-shadow(0 -1px 1px rgba(214,222,235,0.3))";

/*
 * ── Floor geometry ─────────────────────────────────────────────────────────
 * The van is photographed at a three-quarter angle, so the floor under it is
 * not horizontal: measured on the artwork, the tyres touch the floor at
 *   front wheel ≈ (18% across, 79% down) and rear wheel ≈ (61% across, 93.5% down).
 * That is a line sloping down ~10.4° to the right. Every shadow and the
 * reflection follow this line, otherwise there is a visible gap under the
 * front wheel and the vehicle looks pasted on.
 */
const FLOOR_ANGLE = "10.4deg";

/**
 * Mirrors the image across the sloped floor line (not a horizontal line), so
 * each tyre's reflection starts exactly at its own contact point.
 * Maths: y' = 0.3657·x − y + 1.45·height  (slope 0.1828 doubled; floor
 * crosses the left edge at 72.5% of the height, doubled = 145%).
 */
const REFLECTION_TRANSFORM = "translateY(145%) matrix(1, 0.3657, 0, -1, 0, 0)";

/**
 * Fades the reflection out with distance from the floor. The gradient runs
 * perpendicular to the floor line; the floor sits at ~20% along it.
 */
const REFLECTION_MASK =
  `linear-gradient(${FLOOR_ANGLE}, rgba(0,0,0,0.3) 20%, rgba(0,0,0,0) 38%)`;

/** Positions a floor element by its centre and lays it along the floor line. */
const alongFloor = ({ centerX, centerY, width, height }) => ({
  left: centerX,
  top: centerY,
  width,
  height,
  transform: `translate(-50%, -50%) rotate(${FLOOR_ANGLE})`,
});

/** Uses the vehicle's own alpha as a mask, so light only lands on the vehicle. */
const maskToVehicle = (imageUrl) => ({
  WebkitMaskImage: `url("${imageUrl}")`,
  maskImage: `url("${imageUrl}")`,
  WebkitMaskSize: "100% 100%",
  maskSize: "100% 100%",
  WebkitMaskRepeat: "no-repeat",
  maskRepeat: "no-repeat",
});

const GranLuxeHeroVehicle = ({
  src,
  srcSet,
  sizes = "100vw",
  alt,
  to,
  cta,
  className = "",
  vehicleClassName = "",
  parallax = false,
  delay = 0,
  enterFrom = -40,
  children,
}) => {
  const shouldReduceMotion = useReducedMotion();

  // The file the browser actually picked from `srcSet`. The reflection and the
  // paint mask reuse it, so they never trigger a second download.
  const [renderedSrc, setRenderedSrc] = useState(null);
  const isLoaded = Boolean(renderedSrc);

  const rememberRenderedSrc = useCallback((image) => {
    setRenderedSrc(image.currentSrc || image.src);
  }, []);

  // Cached images can finish loading before React attaches onLoad, so the ref
  // callback settles the initial state from `complete`.
  const captureImage = useCallback(
    (node) => {
      if (node?.complete && node.naturalWidth) rememberRenderedSrc(node);
    },
    [rememberRenderedSrc]
  );

  // Pointer position over the stage, normalised to -0.5…0.5 on both axes.
  const pointerX = useMotionValue(0);
  const pointerY = useMotionValue(0);
  const springX = useSpring(pointerX, { stiffness: 110, damping: 20, mass: 0.6 });
  const springY = useSpring(pointerY, { stiffness: 110, damping: 20, mass: 0.6 });
  const drift = parallax && !shouldReduceMotion;

  // The vehicle only shifts a few pixels (depth), the light moves much more.
  const vehicleX = useTransform(springX, [-0.5, 0.5], [-6, 6]);
  const vehicleY = useTransform(springY, [-0.5, 0.5], [-3, 3]);
  const keyLightX = useTransform(springX, [-0.5, 0.5], ["-18%", "18%"]);
  const keyLightY = useTransform(springY, [-0.5, 0.5], ["-8%", "8%"]);
  const glareX = useTransform(springX, [-0.5, 0.5], ["-40%", "40%"]);

  const handlePointerMove = useCallback(
    (event) => {
      if (!drift) return;
      const bounds = event.currentTarget.getBoundingClientRect();
      if (!bounds.width || !bounds.height) return;
      pointerX.set((event.clientX - bounds.left) / bounds.width - 0.5);
      pointerY.set((event.clientY - bounds.top) / bounds.height - 0.5);
    },
    [drift, pointerX, pointerY]
  );

  const resetPointer = useCallback(() => {
    pointerX.set(0);
    pointerY.set(0);
  }, [pointerX, pointerY]);

  // Hover is a gentle camera push-in — the vehicle stays planted on the floor.
  const gestureVariants = shouldReduceMotion
    ? { rest: {}, hover: {}, tap: {} }
    : {
        rest: { scale: 1, transition: transitions.base },
        hover: { scale: 1.02, transition: transitions.base },
        tap: { scale: 0.99, transition: { duration: 0.12 } },
      };

  const haloVariants = {
    rest: { opacity: 0.85, scale: 1, transition: transitions.slow },
    hover: { opacity: 1, scale: 1.08, transition: transitions.slow },
  };

  // Sweeps once after the entrance, then again on every hover. Going back to
  // `rest` it is already parked off to the right, so nothing visibly replays.
  const streakVariants = {
    rest: {
      x: "230%",
      transition: { duration: 1.6, ease: "easeInOut", delay: delay + 1 },
    },
    hover: {
      x: ["-100%", "230%"],
      transition: { duration: 1.2, ease: "easeInOut" },
    },
  };

  const glareVariants = {
    rest: { opacity: 0, transition: transitions.slow },
    hover: { opacity: 1, transition: transitions.slow },
  };

  const ctaVariants = {
    rest: { opacity: 0, y: 8, transition: transitions.fast },
    hover: { opacity: 1, y: 0, transition: transitions.fast },
  };

  return (
    <div
      className={cn("tw:relative tw:overflow-hidden", className)}
      style={{ background: STAGE_BASE }}
      onPointerMove={handlePointerMove}
      onPointerLeave={resetPointer}
    >
      <div
        aria-hidden="true"
        className="tw:absolute tw:inset-0"
        style={{ background: STAGE_VIGNETTE }}
      />

      {/* Key light on the backdrop, above and behind the vehicle. */}
      <motion.div
        aria-hidden="true"
        className="tw:absolute tw:left-[-5%] tw:top-[-25%] tw:w-[65%] tw:h-[110%] tw:pointer-events-none"
        style={{
          background: KEY_LIGHT,
          ...(drift ? { x: keyLightX, y: keyLightY } : {}),
        }}
      />

      {/* Entrance layer — opacity/x/scale only, so it never fights the parallax
          and gesture transforms nested below it. */}
      <motion.div
        className={cn("tw:absolute", vehicleClassName)}
        initial={
          shouldReduceMotion
            ? { opacity: 0 }
            : { opacity: 0, x: enterFrom, scale: 0.97 }
        }
        animate={
          shouldReduceMotion ? { opacity: 1 } : { opacity: 1, x: 0, scale: 1 }
        }
        transition={{
          duration: shouldReduceMotion ? 0.3 : 1,
          ease: EASE_OUT,
          delay,
        }}
      >
        <motion.div style={drift ? { x: vehicleX, y: vehicleY } : undefined}>
          <motion.div
            className="tw:relative"
            initial="rest"
            animate="rest"
            whileHover="hover"
            whileTap="tap"
            variants={gestureVariants}
          >
            {/* Grey halo — the "silhouette" light the baked-in banner had. */}
            <motion.div
              aria-hidden="true"
              className="tw:absolute tw:-inset-[28%] tw:pointer-events-none"
              style={{ background: VEHICLE_HALO }}
              variants={haloVariants}
            />

            {/* Floor: light pool, then the body shadow, then the tyre contacts. */}
            <div
              aria-hidden="true"
              className="tw:absolute tw:pointer-events-none"
              style={{
                background: FLOOR_LIGHT_POOL,
                ...alongFloor({ centerX: "40%", centerY: "87%", width: "118%", height: "30%" }),
              }}
            />
            <div
              aria-hidden="true"
              className="tw:absolute tw:pointer-events-none"
              style={{
                background: BODY_SHADOW,
                ...alongFloor({ centerX: "40%", centerY: "86.5%", width: "76%", height: "11%" }),
              }}
            />
            <div
              aria-hidden="true"
              className="tw:absolute tw:pointer-events-none"
              style={{
                background: TYRE_CONTACT_SHADOW,
                ...alongFloor({ centerX: "18%", centerY: "79%", width: "13%", height: "4%" }),
              }}
            />
            <div
              aria-hidden="true"
              className="tw:absolute tw:pointer-events-none"
              style={{
                background: TYRE_CONTACT_SHADOW,
                ...alongFloor({ centerX: "61%", centerY: "93.5%", width: "14%", height: "4.5%" }),
              }}
            />

            <Link
              to={to}
              className="tw:block tw:relative tw:rounded-lg tw:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-white/70"
            >
              {/* Reflection on a polished floor, mirrored along the floor line.
                  Rendered after load so it reuses the already-decoded file. */}
              {isLoaded ? (
                <img
                  src={renderedSrc}
                  alt=""
                  aria-hidden="true"
                  draggable="false"
                  className="tw:pointer-events-none tw:absolute tw:top-0 tw:left-0 tw:w-full tw:h-auto tw:blur-[2px] tw:select-none"
                  style={{
                    transform: REFLECTION_TRANSFORM,
                    transformOrigin: "0 0",
                    WebkitMaskImage: REFLECTION_MASK,
                    maskImage: REFLECTION_MASK,
                  }}
                />
              ) : null}

              <motion.img
                ref={captureImage}
                src={src}
                srcSet={srcSet}
                sizes={srcSet ? sizes : undefined}
                alt={alt}
                draggable="false"
                decoding="async"
                onLoad={(event) => rememberRenderedSrc(event.currentTarget)}
                className="tw:relative tw:w-full tw:h-auto tw:object-contain tw:select-none"
                style={{ filter: VEHICLE_GRADE }}
                initial={false}
                animate={{ opacity: isLoaded ? 1 : 0 }}
                transition={transitions.slow}
              />

              {/* Showroom light on the paint: a sweeping streak plus a glare
                  that follows the pointer. Masked to the vehicle's shape. */}
              {isLoaded && !shouldReduceMotion ? (
                <div
                  aria-hidden="true"
                  className="tw:pointer-events-none tw:absolute tw:inset-0 tw:overflow-hidden"
                  style={maskToVehicle(renderedSrc)}
                >
                  <motion.div
                    className="tw:absolute tw:inset-y-0 tw:left-0 tw:w-[45%]"
                    style={{ background: LIGHT_STREAK }}
                    initial={{ x: "-100%" }}
                    variants={streakVariants}
                  />
                  {drift ? (
                    <motion.div
                      className="tw:absolute tw:inset-y-[-20%] tw:left-[15%] tw:w-[70%]"
                      style={{ background: PAINT_GLARE, x: glareX }}
                      variants={glareVariants}
                    />
                  ) : null}
                </div>
              ) : null}

              {cta ? (
                <motion.span
                  className="tw:absolute tw:bottom-[13%] tw:left-[8%] tw:inline-flex tw:items-center tw:gap-2 tw:whitespace-nowrap tw:rounded-full tw:border tw:border-white/30 tw:bg-black/70 tw:backdrop-blur-md tw:px-4 tw:py-2 tw:text-xs tw:font-semibold tw:uppercase tw:tracking-[2px] tw:text-white"
                  variants={ctaVariants}
                >
                  {cta}
                  <i className="fa fa-arrow-right" aria-hidden="true"></i>
                </motion.span>
              ) : null}
            </Link>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Film grain over the whole stage — hides the cut-out edge and banding. */}
      <div
        aria-hidden="true"
        className="tw:absolute tw:inset-0 tw:pointer-events-none tw:opacity-[0.05]"
        style={{ backgroundImage: FILM_GRAIN }}
      />

      {children}
    </div>
  );
};

export default GranLuxeHeroVehicle;
